// Confirm — the one "are you sure?" the workspace asks before something is deleted for good.
import React, { useEffect, useRef, useState } from 'react';

export interface ConfirmAsk {
  title: string;
  body: string;
  /** The button's words, e.g. "Delete". */
  action: string;
  run: () => Promise<void>;
}

const Confirm: React.FC<{ ask: ConfirmAsk | null; onClose: () => void }> = ({ ask, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setBusy(false); setError(null);
    if (!ask) return;
    cancel.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [ask, onClose]);
  if (!ask) return null;
  const go = async () => {
    setBusy(true); setError(null);
    try { await ask.run(); onClose(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };
  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ws-modal" role="alertdialog" aria-modal="true" aria-labelledby="ws-confirm-title" aria-describedby="ws-confirm-body">
        <h3 id="ws-confirm-title">{ask.title}</h3>
        <p id="ws-confirm-body" className="ws-modal-lede">{ask.body}</p>
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          <button ref={cancel} type="button" className="ws-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="ws-btn ws-danger" onClick={go} disabled={busy}>{busy ? 'Deleting…' : ask.action}</button>
        </div>
      </div>
    </div>
  );
};

export default Confirm;
